import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import DDPInspectorContext from './DDPInspectorContext'
import DDPInspectorLauncher from './DDPInspectorLauncher'
import DDPInspectorDrawer from './DDPInspectorDrawer'

export default function ActiveDDPInspectorProvider ({ children, isActive = true }) {
  const [entriesById, setEntriesById] = useState({})
  const [activeEntryId, setActiveEntryId] = useState(null)
  const [componentAccessorsByEntryId, setComponentAccessorsByEntryId] = useState({})

  const register = useCallback((id, entry) => {
    if (!id) return
    setEntriesById(prev => ({ ...prev, [id]: { id, ...entry } }))
  }, [])

  const unregister = useCallback((id) => {
    if (!id) return
    setEntriesById(prev => {
      if (!(id in prev)) return prev
      const { [id]: _omit, ...rest } = prev
      return rest
    })
    setComponentAccessorsByEntryId(prev => {
      if (!(id in prev)) return prev
      const { [id]: _omit, ...rest } = prev
      return rest
    })
    setActiveEntryId(current => (current === id ? null : current))
  }, [])

  const setComponentAccessors = useCallback((entryId, componentInstanceId, payload) => {
    if (!entryId || !componentInstanceId) return
    setComponentAccessorsByEntryId(prev => {
      const forEntry = prev[entryId] || {}
      return { ...prev, [entryId]: { ...forEntry, [componentInstanceId]: payload } }
    })
  }, [])

  const clearComponentAccessors = useCallback((entryId, componentInstanceId) => {
    if (!entryId || !componentInstanceId) return
    setComponentAccessorsByEntryId(prev => {
      const forEntry = prev[entryId]
      if (!forEntry || !(componentInstanceId in forEntry)) return prev
      const { [componentInstanceId]: _omit, ...rest } = forEntry
      if (!Object.keys(rest).length) {
        const { [entryId]: _entryOmit, ...others } = prev
        return others
      }
      return { ...prev, [entryId]: rest }
    })
  }, [])

  const openDrawerFor = useCallback((id) => setActiveEntryId(id), [])
  const closeDrawer = useCallback(() => setActiveEntryId(null), [])

  const entries = useMemo(() => Object.values(entriesById), [entriesById])
  const activeEntry = activeEntryId ? entriesById[activeEntryId] || null : null

  const value = useMemo(() => ({
    isActive,
    register,
    unregister,
    entries,
    openDrawerFor,
    closeDrawer,
    activeEntryId,
    setComponentAccessors,
    clearComponentAccessors,
    componentAccessorsByEntryId
  }), [isActive, register, unregister, entries, openDrawerFor, closeDrawer, activeEntryId,
    setComponentAccessors, clearComponentAccessors, componentAccessorsByEntryId])

  return (
    <DDPInspectorContext.Provider value={value}>
      {children}
      {isActive && <DDPInspectorLauncher />}
      {isActive && <DDPInspectorDrawer entry={activeEntry} onClose={closeDrawer} />}
    </DDPInspectorContext.Provider>
  )
}

ActiveDDPInspectorProvider.propTypes = {
  children: PropTypes.node,
  isActive: PropTypes.bool
}
